import type { Json } from '../../lib/database.types'
import { arr, num, obj, str } from '../../lib/json'

/**
 * A project's reconciliation map, as its own nightly report states it.
 *
 * Since ops contract v10 (98h-infra ops/contract/v10/recon.sql), a project that
 * reconciles anything adds `recon` to the summary it reports, and the fleet keeps that
 * summary whole in `data_project_health.summary`. So this is not a copy of the decision
 * that has to be kept in step with it: it is what the checks ran with last night. A
 * project that reconciles nothing sends no `recon` key, and has no map here.
 *
 *   entities    which NetSuite subsidiaries make up each entity
 *   tolerances  how close two figures must be: `copy` (a copy of NetSuite against
 *               NetSuite, an absolute amount) and `cross_system` (two different systems,
 *               a percentage while the period is open and another once it is closed)
 *   notes       the rest of the decision as text by topic: who owns a variance, the
 *               fiscal calendar, the lock rule
 *
 * Topics and comparisons are data. A topic this file has no label for is shown under its
 * own name, never dropped (product rule 2).
 */

export interface ReconEntity {
  entity: string
  subsidiaries: string[]
  notes: string | null
}

export interface ReconTolerance {
  comparison: string
  /** "within $1", "3% while open, 1% once closed" -- the rule in words. */
  rule: string
  notes: string | null
}

export interface ReconNote {
  topic: string
  label: string
  note: string
}

export interface ReconMap {
  entities: ReconEntity[]
  tolerances: ReconTolerance[]
  notes: ReconNote[]
  /** One line for the closed state: "UST 13 · Equip 12 · 98 Holdings 1, 6, 14, 16". */
  line: string
}

const COMPARISON: Record<string, string> = {
  copy: 'Copies of NetSuite',
  cross_system: 'Across systems',
}

/** In the order a reader wants them; any other topic follows, by name. */
const TOPICS: ReadonlyArray<[string, string]> = [
  ['owner', 'Variances'],
  ['lock', 'Close and lock'],
  ['calendar', 'Fiscal calendar'],
  ['source_totals', 'NetSuite totals'],
  ['unmapped', 'Not mapped'],
]

function dollars(n: number): string {
  return Number.isInteger(n) ? `$${n}` : `$${n.toFixed(2)}`
}

function rule(t: Record<string, Json | undefined>): string {
  const parts: string[] = []
  const abs = num(t.abs_amount)
  const open = num(t.open_pct)
  const closed = num(t.closed_pct)
  if (abs !== null) parts.push(`within ${dollars(abs)}`)
  if (open !== null && closed !== null) parts.push(`${open}% while open, ${closed}% once closed`)
  else if (open !== null) parts.push(`${open}% while open`)
  else if (closed !== null) parts.push(`${closed}% once closed`)
  return parts.join('; ') || 'no rule stated'
}

export function toReconMap(summary: Json | undefined | null): ReconMap | null {
  const recon = obj(obj(summary)?.recon)
  if (!recon) return null

  const entities: ReconEntity[] = arr(recon.entities).flatMap((e) => {
    const o = obj(e)
    const entity = o ? str(o.entity) : null
    if (!o || !entity) return []
    const subsidiaries = arr(o.subsidiaries).map((s) => str(s)).filter((s): s is string => s !== null)
    return [{ entity, subsidiaries, notes: str(o.notes) }]
  })

  const tolerances: ReconTolerance[] = arr(recon.tolerances).flatMap((t) => {
    const o = obj(t)
    const comparison = o ? str(o.comparison) : null
    if (!o || !comparison) return []
    return [{ comparison: COMPARISON[comparison] ?? comparison, rule: rule(o), notes: str(o.notes) }]
  })

  const raw = obj(recon.notes) ?? {}
  const known = new Map(TOPICS)
  const notes: ReconNote[] = Object.entries(raw)
    .flatMap(([topic, v]) => {
      const note = str(v)
      return note ? [{ topic, label: known.get(topic) ?? topic, note }] : []
    })
    .sort((a, b) => {
      const ia = TOPICS.findIndex(([t]) => t === a.topic)
      const ib = TOPICS.findIndex(([t]) => t === b.topic)
      return (ia < 0 ? TOPICS.length : ia) - (ib < 0 ? TOPICS.length : ib) || a.topic.localeCompare(b.topic)
    })

  if (entities.length === 0 && tolerances.length === 0 && notes.length === 0) return null

  const line = entities.map((e) => `${e.entity} ${e.subsidiaries.join(', ')}`.trim()).join(' · ')
  return { entities, tolerances, notes, line }
}
